import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, Loader2, Phone, Clock, MapPin, ShieldAlert, X } from "lucide-react";

const Dato = ({ icono: Icono, etiqueta, valor, testid }) => (
  <div className="flex items-start gap-3">
    <Icono className="h-4 w-4 text-primary mt-0.5 shrink-0" />
    <div className="min-w-0">
      <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{etiqueta}</p>
      <p className="text-sm font-semibold mt-0.5 break-words" data-testid={testid}>{valor || "Sin informar"}</p>
    </div>
  </div>
);

export default function BuscarAlumno() {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [ficha, setFicha] = useState(null);
  const [cargandoFicha, setCargandoFicha] = useState(false);

  useEffect(() => {
    const texto = q.trim();
    if (texto.length < 2) {
      setResultados(null);
      return;
    }
    setBuscando(true);
    const t = setTimeout(() => {
      api.post("/profesor/alumnos/buscar", { q: texto })
        .then(({ data }) => setResultados(data))
        .catch((e) => toastError(e))
        .finally(() => setBuscando(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const toastError = (e) => {
    import("sonner").then(({ toast }) =>
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "No se ha podido buscar."));
    setBuscando(false);
  };

  const abrir = async (alumno) => {
    setCargandoFicha(true);
    setFicha({ id: alumno.id, nombre: alumno.nombre });
    try {
      const { data } = await api.get(`/profesor/alumnos/${alumno.id}/contacto`);
      setFicha(data);
    } catch (e) {
      setFicha(null);
      toastError(e);
    } finally {
      setCargandoFicha(false);
    }
  };

  return (
    <div data-testid="buscar-alumno-page">
      <PageHeader title="Buscar alumno"
        subtitle="Consulta de contacto y horarios de los alumnos de tu sede. Solo lectura." />

      <Card className="rounded-2xl p-6 border-border shadow-sm mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 rounded-xl"
            placeholder="Escribe el nombre del alumno…" data-testid="buscar-alumno-input" />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          No hace falta poner las tildes. Se buscan solo los alumnos activos de tu sede.
        </p>
      </Card>

      {buscando && (
        <Loader2 className="h-5 w-5 animate-spin text-primary" data-testid="buscar-alumno-cargando" />
      )}

      {!buscando && resultados && resultados.length === 0 && (
        <Card className="rounded-2xl p-6 border-border shadow-sm">
          <p className="text-sm text-muted-foreground" data-testid="buscar-alumno-sin-resultados">
            Ningún alumno de tu sede coincide con esa búsqueda.
          </p>
        </Card>
      )}

      {!buscando && resultados && resultados.length > 0 && (
        <div className="grid gap-2 mb-8" data-testid="buscar-alumno-resultados">
          {resultados.map((a) => (
            <button key={a.id} onClick={() => abrir(a)} data-testid={`resultado-${a.id}`}
              className={`flex items-center justify-between gap-3 rounded-xl border px-4 py-3 text-sm text-left transition-colors duration-200 ${
                ficha?.id === a.id ? "border-primary bg-accent/50 font-semibold" : "border-border hover:bg-muted"
              }`}>
              <span className="min-w-0 truncate">{a.nombre}</span>
              <span className="text-xs text-muted-foreground shrink-0">Ver contacto</span>
            </button>
          ))}
        </div>
      )}

      {ficha && (
        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="ficha-contacto">
          <div className="flex items-start justify-between gap-3 mb-5">
            <div className="min-w-0">
              <h2 className="font-display text-2xl font-extrabold break-words"
                data-testid="ficha-contacto-nombre">
                {ficha.nombre}
              </h2>
              <Badge variant="outline" className="rounded-full mt-2">Solo lectura</Badge>
            </div>
            <Button variant="ghost" size="icon" className="rounded-full shrink-0"
              onClick={() => setFicha(null)} data-testid="ficha-contacto-cerrar">
              <X className="h-4 w-4" />
            </Button>
          </div>

          {cargandoFicha ? (
            <Loader2 className="h-5 w-5 animate-spin text-primary" data-testid="ficha-contacto-cargando" />
          ) : (
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                  Clases y horarios
                </p>
                {(ficha.turnos || []).length === 0 && (ficha.clases_sin_turno || []).length === 0 ? (
                  <p className="text-sm text-muted-foreground" data-testid="ficha-contacto-sin-turnos">
                    No tiene ningún horario registrado.
                  </p>
                ) : (
                  <div className="space-y-2" data-testid="ficha-contacto-turnos">
                    {(ficha.turnos || []).map((t, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-muted px-4 py-3 text-sm">
                        <span className="font-semibold">{t.clase}</span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <Clock className="h-3.5 w-3.5" />
                          {t.dia} {t.hora_inicio}{t.hora_fin ? `–${t.hora_fin}` : ""}
                        </span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" /> {t.sede}
                        </span>
                      </div>
                    ))}
                    {(ficha.clases_sin_turno || []).map((c, i) => (
                      <div key={`s${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-xl bg-muted px-4 py-3 text-sm">
                        <span className="font-semibold">{c.clase}</span>
                        <span className="text-muted-foreground">Sin horario concretado</span>
                        <span className="flex items-center gap-1.5 text-muted-foreground">
                          <MapPin className="h-3.5 w-3.5" /> {c.sede}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid sm:grid-cols-2 gap-5 pt-5 border-t border-border">
                <Dato icono={Phone} etiqueta="Padre, madre o tutor"
                  valor={ficha.tutor?.nombre} testid="ficha-contacto-tutor" />
                <Dato icono={Phone} etiqueta="Teléfono del tutor"
                  valor={ficha.tutor?.telefono} testid="ficha-contacto-telefono" />
              </div>

              <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-4">
                <div className="flex items-start gap-3">
                  <ShieldAlert className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs uppercase tracking-wider font-semibold text-amber-700 dark:text-amber-400">
                      Contacto de emergencia
                    </p>
                    <p className="text-sm font-semibold mt-0.5" data-testid="ficha-contacto-emergencia">
                      {ficha.emergencia?.telefono || "Sin informar"}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Es el mismo número del tutor, no un contacto distinto verificado.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
